import { Fragment, useEffect, useRef, useState } from 'react';
import { useStageGuard } from '../../hooks/useStageGuard';
import LandingNav from './LandingNav';
import LandingFinalCta from './LandingFinalCta';
import LandingFooter from './LandingFooter';
import { useStartAudit } from './useStartAudit';
import './landing.css';
import face1 from '@/assets/landing/face-1.png';
import face2 from '@/assets/landing/face-2.png';
import face3 from '@/assets/landing/face-3.png';
import face4 from '@/assets/landing/face-4.png';
import casePhoto from '@/assets/landing/case-photo.png';

const STATS = [
  { value: 1, prefix: '', suffix: 'h', label: 'average turnaround' },
  { value: 40, prefix: '$', suffix: 'K+', label: 'avg. losses found / month' },
  { value: 2000, prefix: '', suffix: '+', label: 'audits delivered' },
  { value: 10, prefix: '', suffix: '+', label: 'prioritized recommendations' },
];

/** A number that counts up from 0 → value once it scrolls into view. */
function CountUp({ value, prefix = '', suffix = '', className }: { value: number; prefix?: string; suffix?: string; className?: string }) {
  const [n, setN] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setN(value);
      return;
    }
    let raf = 0;
    let done = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || done) return;
          done = true;
          io.unobserve(e.target);
          const duration = 900;
          const start = performance.now();
          const step = (now: number) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
            setN(Math.round(value * eased));
            if (t < 1) raf = requestAnimationFrame(step);
          };
          raf = requestAnimationFrame(step);
        });
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value]);

  return <span className={className} ref={ref}>{prefix}{n.toLocaleString('en-US')}{suffix}</span>;
}

const FAQ_ITEMS = [
  {
    q: 'What data do I need to connect?',
    a: 'Minimum: RevenueCat or equivalent (subscription data) plus at least one ad account (Google Ads or Meta). The more sources, the deeper the analysis — AppsFlyer, GA4, Amplitude and Mixpanel all enrich the report.',
  },
  {
    q: 'Is this right for my business?',
    a: 'The audit works best for SaaS, mobile apps and D2C subscriptions with $5K+ MRR. If you run e-commerce or another model, write to us and we’ll review it individually.',
  },
  {
    q: 'What if the audit finds nothing critical?',
    a: 'That’s valuable too — it means the business is healthy and you can focus on growth. Of 2,000+ audits delivered, none ended with an empty report. The average finding is $40K+ of monthly losses.',
  },
  {
    q: 'How safe is it to share my data?',
    a: 'Access is via OAuth with read-only scope — we can never modify your data. We sign an NDA before any work begins, and your data is never shared with third parties.',
  },
  {
    q: 'What do I get on the call?',
    a: 'A live walkthrough with an analyst: we explain every finding, answer questions and help you prioritize the first steps. You leave with clear next actions — not just a PDF.',
  },
];

export default function LandingView() {
  const handleStart = useStartAudit();
  // For an authenticated user landing on `/`, jump them straight to wherever
  // they left off in the workflow (only kicks in for irreversible stages —
  // see useStageGuard). Unauthenticated visitors fall through and see the
  // marketing landing page as normal.
  useStageGuard('/');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Reveal-on-scroll: add `.in` to `.reveal` blocks and `.reveal-item` elements as
  // they enter the viewport. Items in the same parent get a small staggered delay
  // so cards/steps cascade in rather than appearing all at once.
  useEffect(() => {
    const counters = new Map<Element, number>();
    const items = document.querySelectorAll<HTMLElement>('.advi-landing .reveal-item');
    items.forEach((el) => {
      const parent = el.parentElement;
      if (!parent) return;
      const n = counters.get(parent) ?? 0;
      el.style.transitionDelay = `${(n * 0.07).toFixed(2)}s`;
      counters.set(parent, n + 1);
    });

    const els = document.querySelectorAll<HTMLElement>('.advi-landing .reveal, .advi-landing .reveal-item');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="advi-landing">
      {/* NAV */}
      <LandingNav />

      {/* HERO */}
      <header className="hero">
        <div className="wrap hero-grid">
          <div>
            <span className="pill anim-up" style={{ animationDelay: '.04s' }}><span className="dot"></span>Report ready in 1 hour</span>
            <h1 className="anim-up" style={{ animationDelay: '.10s' }}>Your business is leaking money. <em>We'll find exactly where.</em></h1>
            <p className="hero-sub anim-up" style={{ animationDelay: '.18s' }}>An express audit built on your real data — retention, unit economics, ad channels, conversion funnel. You walk away with a concrete plan, not generic advice.</p>
            <div className="hero-cta anim-up" style={{ animationDelay: '.26s' }}>
              <button className="btn btn-dark btn-lg" onClick={handleStart}>Get my audit <span className="arr">→</span></button>
            </div>
            <div className="hero-proof anim-up" style={{ animationDelay: '.34s' }}>
              <div className="faces">
                <img className="face" src={face1} alt="Founder" />
                <img className="face" src={face2} alt="Founder" />
                <img className="face" src={face3} alt="Founder" />
                <img className="face" src={face4} alt="Founder" />
              </div>
              <p className="proof-txt"><strong>2,000+ founders</strong> audited — we find <strong>$40K+/mo</strong> in losses on average</p>
            </div>
          </div>

          <div className="hero-visual anim-up" style={{ animationDelay: '.20s' }}>
            <div className="hero-photo">
              <div className="rpt">
                <div className="rpt-bar">
                  <span className="rpt-logo">
                    <svg viewBox="0 0 60 60" fill="none">
                      <path d="M34.715 25.93c-1.75 0-3.47-.71-4.71-1.96a6.5 6.5 0 0 1-1.86-4.6L28.21 0l5.66.02-.06 17.19c0 .5.19.96.53 1.31.35.35.83.55 1.32.55.45 0 .89-.16 1.23-.47L49.71 7.1l3.77 4.21-14.46 12.94c-1.22 1.1-2.71 1.68-4.3 1.68Z" fill="#FF470E" />
                      <path d="M26.14 59.98l.06-17.18c0-.5-.19-.96-.53-1.3a1.85 1.85 0 0 0-1.32-.55c-.45 0-.89.16-1.23.47L10.29 52.9l-3.77-4.22 14.46-12.93c1.22-1.1 2.71-1.68 4.3-1.68 1.75 0 3.47.71 4.71 1.96 1.2 1.2 1.86 2.84 1.86 4.6l-.07 19.37-5.66-.02Z" fill="#FF470E" />
                      <path d="M0 31.79l.02-5.66 17.18.06c.74 0 1.39-.42 1.69-1.1.3-.67.18-1.43-.31-1.98L7.1 10.29l4.21-3.77 12.94 14.46c1.74 1.95 2.16 4.63 1.1 7.02-1.07 2.38-3.35 3.86-5.96 3.86L0 31.8Z" fill="#FF470E" />
                      <path d="M35.74 39.02c-1.74-1.95-2.16-4.63-1.1-7.02 1.07-2.38 3.35-3.86 5.96-3.86l19.4.07-.02 5.65-17.18-.06c-.74 0-1.39.42-1.69 1.1-.3.67-.18 1.43.31 1.98l11.48 12.83-4.21 3.77-12.95-14.46Z" fill="#FF470E" />
                    </svg>
                  </span>
                  <span className="rpt-name">My Project</span>
                  <span className="rpt-tabs"><b className="on">Report</b><b>Data</b><b>Blocks</b></span>
                  <span className="rpt-pdf">PDF</span>
                </div>
                <div className="rpt-body">
                  <div className="rpt-row">
                    <div className="rpt-gauge">
                      <svg viewBox="0 0 100 58">
                        <defs><linearGradient id="gaug" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#BE4030" /><stop offset=".5" stopColor="#BF7A2C" /><stop offset="1" stopColor="#2E7A53" /></linearGradient></defs>
                        <path d="M8 53 A44 44 0 0 1 92 53" fill="none" stroke="#eee9e7" strokeWidth="9" strokeLinecap="round" />
                        <path d="M8 53 A44 44 0 0 1 92 53" fill="none" stroke="url(#gaug)" strokeWidth="9" strokeLinecap="round" />
                        <circle cx="35" cy="12" r="5.5" fill="#fff" stroke="#14130F" strokeWidth="2.5" />
                      </svg>
                      <div className="rpt-gnum">39</div>
                      <div className="rpt-glabel">Health Score</div>
                    </div>
                    <div className="rpt-ins">
                      <div className="rpt-ins-h">Key insights</div>
                      <div className="ri"><span className="ri-d"></span><span className="ri-b" style={{ width: '94%' }}></span></div>
                      <div className="ri"><span className="ri-d"></span><span className="ri-b" style={{ width: '78%' }}></span></div>
                      <div className="ri"><span className="ri-d"></span><span className="ri-b" style={{ width: '88%' }}></span></div>
                      <div className="ri"><span className="ri-d"></span><span className="ri-b" style={{ width: '64%' }}></span></div>
                      <div className="ri"><span className="ri-d"></span><span className="ri-b" style={{ width: '82%' }}></span></div>
                      <div className="ri"><span className="ri-d"></span><span className="ri-b" style={{ width: '70%' }}></span></div>
                    </div>
                  </div>
                  <div className="rpt-stats">
                    <div className="ms"><div className="ms-n">$225K</div><div className="ms-l">Lifetime rev</div></div>
                    <div className="ms"><div className="ms-n">$658K</div><div className="ms-l">Ad spend</div></div>
                    <div className="ms"><div className="ms-n">937</div><div className="ms-l">Active subs</div></div>
                  </div>
                  <div className="rpt-funnel">
                    <div className="rf-h"><span className="rf-dot"></span>Conversion path</div>
                    <div className="rf-wrap">
                      <svg className="rf-svg" viewBox="0 0 300 66" preserveAspectRatio="none"><defs><linearGradient id="funn" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#F0825A" stopOpacity="0" /><stop offset="0.16" stopColor="#F0825A" stopOpacity=".5" /><stop offset="1" stopColor="#F0825A" stopOpacity=".14" /></linearGradient></defs><path d="M6 3 L118 26 L294 31 L294 35 L118 40 L6 63 Z" fill="url(#funn)" /><line x1="118" y1="3" x2="118" y2="63" stroke="rgba(20,19,15,.12)" strokeWidth="1" strokeDasharray="3 3" /></svg>
                      <div className="rf-stages">
                        <div className="rf-s s1"><b>225,364</b><span>Installs</span></div>
                        <div className="rf-s s2"><b>16,115</b><span>Trial starts</span></div>
                        <div className="rf-s s3"><b>295</b><span>Converts</span></div>
                      </div>
                    </div>
                  </div>
                  <div className="rpt-cards">
                    <div className="mc"><div className="mc-ring"><svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="15.9155" fill="none" stroke="#eee9e7" strokeWidth="4" /><circle cx="20" cy="20" r="15.9155" fill="none" stroke="#D14A36" strokeWidth="4" strokeLinecap="round" strokeDasharray="39 100" /></svg><b>39</b></div><div className="mc-l">Health Score</div></div>
                    <div className="mc"><div className="mc-ring"><svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="15.9155" fill="none" stroke="#eee9e7" strokeWidth="4" /><circle cx="20" cy="20" r="15.9155" fill="none" stroke="#D14A36" strokeWidth="4" strokeLinecap="round" strokeDasharray="29 100" /></svg><b>29</b></div><div className="mc-l">LTV:CAC</div></div>
                    <div className="mc"><div className="mc-ring"><svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="15.9155" fill="none" stroke="#eee9e7" strokeWidth="4" /><circle cx="20" cy="20" r="15.9155" fill="none" stroke="#D99A2B" strokeWidth="4" strokeLinecap="round" strokeDasharray="53 100" /></svg><b>53</b></div><div className="mc-l">Trial Conv</div></div>
                    <div className="mc"><div className="mc-ring"><svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="15.9155" fill="none" stroke="#eee9e7" strokeWidth="4" /><circle cx="20" cy="20" r="15.9155" fill="none" stroke="#D14A36" strokeWidth="4" strokeLinecap="round" strokeDasharray="42 100" /></svg><b>42</b></div><div className="mc-l">Retention</div></div>
                  </div>
                </div>
              </div>
            </div>

            {/* floating glass: health score */}
            <div className="glass g-score">
              <div className="g-cap">Business Health Score</div>
              <div className="g-score-row">
                <div className="g-num">22<span>/100</span></div>
              </div>
              <div className="g-bar"><i></i></div>
              <div className="g-state">Critical — needs immediate action</div>
            </div>

            {/* floating glass: metric */}
            <div className="glass g-metric">
              <div className="g-cap">Monthly churn</div>
              <div className="g-mrow">
                <div className="g-mval">29%</div>
                <div className="g-mtag">3.6×</div>
              </div>
              <div className="g-msub">over benchmark</div>
              <div className="g-spark">
                <i style={{ height: '40%' }}></i><i style={{ height: '52%' }}></i><i style={{ height: '48%' }}></i><i style={{ height: '66%' }}></i><i style={{ height: '74%' }}></i><i style={{ height: '88%' }}></i><i style={{ height: '100%' }}></i>
              </div>
            </div>

            {/* floating glass: recommendation */}
            <div className="glass g-rec">
              <div className="g-cap">Top recommendation</div>
              <div className="g-rec-row">
                <span className="g-rec-ic"><svg viewBox="0 0 24 24"><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.6a5.66 5.66 0 0 0-.77-.05A5.67 5.67 0 1 0 15.54 15V8.69a7.29 7.29 0 0 0 4.27 1.37V7a4.28 4.28 0 0 1-3.21-1.18z" /></svg></span>
                <span>Pause TikTok, AppLovin &amp; Snapchat → save <strong>$70K/mo</strong> immediately.</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* PROOF STRIP */}
      <div className="strip">
        <div className="strip-in">
          {STATS.map((s, i) => (
            <Fragment key={s.label}>
              {i > 0 && <span className="strip-div"></span>}
              <div className="stat">
                <CountUp className="stat-n" value={s.value} prefix={s.prefix} suffix={s.suffix} />
                <span className="stat-l">{s.label}</span>
              </div>
            </Fragment>
          ))}
        </div>
      </div>

      {/* PROBLEM */}
      <section className="block wrap" id="problem">
        <div className="block-head reveal-item">
          <span className="eyebrow">The problem</span>
          <h2 className="sec" style={{ maxWidth: 'none', whiteSpace: 'nowrap' }}>You're spending money.<br />You don't know <em>why MRR is falling.</em></h2>
        </div>
        <div className="bento problem-bento">
          <div className="card reveal-item">
            <div className="card-ic"><svg viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /><path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" /></svg></div>
            <h3>Data everywhere, meaning nowhere</h3>
            <p>RevenueCat, AppsFlyer, Google Ads, Meta — every tool counts differently. You stare at dashboards and still can't tell why the business won't grow.</p>
          </div>
          <div className="card reveal-item">
            <div className="card-ic"><svg viewBox="0 0 24 24"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5Z" /></svg></div>
            <h3>Ad spend burns the budget</h3>
            <p>You know how much you spend. You don't know which channel quietly wastes it. On average 30–40% of ad budget flows into negative-ROI channels.</p>
          </div>
          <div className="card reveal-item">
            <div className="card-ic"><svg viewBox="0 0 24 24"><path d="M3 4h18l-7 8.5V20l-4-2v-5.5L3 4Z" /></svg></div>
            <h3>The funnel leaks invisibly</h3>
            <p>Users drop off — but at which step? Why is churn climbing? Without industry benchmarks you can't tell what's normal and what's a fire.</p>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="block wrap" id="what">
        <div className="block-head reveal-item">
          <span className="eyebrow">What you get</span>
          <h2 className="sec" style={{ maxWidth: 'none' }}><span style={{ whiteSpace: 'nowrap' }}>Not a report for the shelf —</span><br /><em style={{ whiteSpace: 'nowrap' }}>a concrete action plan.</em></h2>
        </div>
        <div className="bento get-bento">

          {/* wide: health score + grades */}
          <div className="card plain b-wide reveal-item">
            <div className="num">01</div>
            <div className="feat-h">Health Score &amp; grades across 5 zones</div>
            <p className="feat-p">An overall business-health score plus A–F grades for Activation, Acquisition, Referral, Revenue and Retention — so you see what's on fire at a glance.</p>
            <div className="mk" style={{ marginTop: 20 }}>
              <div className="mk-grades">
                <div className="mk-grade"><div className="a">Activ.</div><div className="v gB">B+</div></div>
                <div className="mk-grade"><div className="a">Acquis.</div><div className="v gC">C</div></div>
                <div className="mk-grade"><div className="a">Referral</div><div className="v gC">C−</div></div>
                <div className="mk-grade"><div className="a">Revenue</div><div className="v gD">D+</div></div>
                <div className="mk-grade"><div className="a">Retention</div><div className="v gF">F</div></div>
              </div>
            </div>
          </div>

          {/* third: unit economics */}
          <div className="card sky b-third reveal-item">
            <div className="num">02</div>
            <div className="feat-h">Real unit economics</div>
            <p className="feat-p">LTV, CAC, payback &amp; ARPU split by channel, platform and geo — against industry benchmarks.</p>
            <div className="mk ue-row" style={{ marginTop: 18 }}>
              <div className="ue-tile">
                <div className="ue-cap">LTV / CAC</div>
                <div className="ue-n crit">0.14<span className="u">×</span></div>
                <div className="ue-bar"><i className="crit" style={{ width: '5%' }} /></div>
                <div className="ue-sub">Target ≥ 3.0×</div>
              </div>
              <div className="ue-tile">
                <div className="ue-cap">LTV today</div>
                <div className="ue-n"><span className="u">$</span>17</div>
                <div className="ue-bar"><i className="warn" style={{ width: '20%' }} /></div>
                <div className="ue-sub">Industry ~$84</div>
              </div>
            </div>
          </div>

          {/* third: channel breakdown */}
          <div className="card mint b-third card-fill reveal-item">
            <div className="num">03</div>
            <div className="feat-h">Every ad channel, judged</div>
            <p className="feat-p">CPA, trial rate &amp; LTV/CAC per channel with a verdict: scale, optimize or stop now.</p>
            <div className="mk mk-rows" style={{ marginTop: 18 }}>
              <div className="mk-row"><span className="mk-ch"><span className="mk-ic"><svg viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg></span>Google IAA</span><span className="mk-cpa">$29</span><span className="mk-v v-scale">Scale 5×</span></div>
              <div className="mk-row"><span className="mk-ch"><span className="mk-ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 12c-2-2.67-4-4-6-4a4 4 0 1 0 0 8c2 0 4-1.33 6-4Zm0 0c2 2.67 4 4 6 4a4 4 0 0 0 0-8c-2 0-4 1.33-6 4Z" /></svg></span>Meta</span><span className="mk-cpa">$145</span><span className="mk-v v-keep">Optimize</span></div>
              <div className="mk-row"><span className="mk-ch"><span className="mk-ic"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.6a5.66 5.66 0 0 0-.77-.05A5.67 5.67 0 1 0 15.54 15V8.69a7.29 7.29 0 0 0 4.27 1.37V7a4.28 4.28 0 0 1-3.21-1.18z" /></svg></span>TikTok</span><span className="mk-cpa">$427</span><span className="mk-v v-stop">Stop</span></div>
            </div>
          </div>

          {/* half: funnel & churn */}
          <div className="card sand b-wide reveal-item">
            <div className="num">04</div>
            <div className="feat-h">Funnel &amp; churn analysis</div>
            <p className="feat-p">Exactly where users drop off — install → trial → paid → retention — each step against benchmark, with the one bottleneck that matters most.</p>
            <div className="mk mk-funnel" style={{ marginTop: 18 }}>
              <div className="fn-row"><div className="fn-bar" style={{ width: '100%' }}>Install</div><span className="fn-pct">100%</span></div>
              <div className="fn-row"><div className="fn-bar" style={{ width: '38%', opacity: .8 }}>Trial</div><span className="fn-pct">7.1%</span></div>
              <div className="fn-row"><div className="fn-bar" style={{ width: '24%', opacity: .6 }}>Paid</div><span className="fn-pct">3.8%</span></div>
              <div className="fn-row"><div className="fn-bar" style={{ width: '10%', background: 'var(--crit)' }}>Retained</div><span className="fn-pct">1.1%</span></div>
            </div>
          </div>

          {/* half: scenarios */}
          <div className="card lav b-third reveal-item">
            <div className="num">05</div>
            <div className="feat-h">12-month scenario modeling</div>
            <p className="feat-p">What happens if nothing changes — versus scenarios A, B and C. MRR, ARR and LTV/CAC in real numbers.</p>
            <div className="mk mk-scen" style={{ marginTop: 18 }}>
              <svg className="scen-svg" viewBox="0 0 260 116" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="scenFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#2E7A53" stopOpacity=".24" />
                    <stop offset="1" stopColor="#2E7A53" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <line x1="0" y1="98" x2="260" y2="98" stroke="rgba(20,19,15,.10)" strokeWidth="1" />
                <line x1="100" y1="6" x2="100" y2="98" stroke="rgba(20,19,15,.20)" strokeWidth="1" strokeDasharray="3 3" />
                <path d="M6 66 L100 60 L250 14 L250 98 L6 98 Z" fill="url(#scenFill)" />
                <path d="M6 66 L100 60" fill="none" stroke="#14130F" strokeWidth="2" strokeLinecap="round" opacity=".55" />
                <path d="M100 60 L250 88" fill="none" stroke="#14130F" strokeWidth="1.6" strokeDasharray="4 4" strokeLinecap="round" opacity=".32" />
                <path d="M100 60 L250 52" fill="none" stroke="#2E7A53" strokeWidth="1.4" strokeLinecap="round" opacity=".38" />
                <path d="M100 60 L250 33" fill="none" stroke="#2E7A53" strokeWidth="1.4" strokeLinecap="round" opacity=".55" />
                <path d="M100 60 L250 14" fill="none" stroke="#2E7A53" strokeWidth="2.6" strokeLinecap="round" />
                <circle cx="100" cy="60" r="3.4" fill="#fff" stroke="#14130F" strokeWidth="1.6" />
                <circle cx="250" cy="14" r="3.8" fill="#2E7A53" />
              </svg>
              <span className="scen-tag up">Scenario C · +218%</span>
              <span className="scen-tag down">Do nothing · −40%</span>
              <span className="scen-tag now">Today</span>
            </div>
          </div>

          {/* full width: recommendations roadmap */}
          <div className="card slate b-wide reveal-item">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 36, alignItems: 'start' }}>
              <div>
                <div className="num">06</div>
                <div className="feat-h">Top-10 recommendations with a roadmap</div>
                <p className="feat-p">What to do this week, in months 1–2, in months 3–6 — every action sized with its expected impact in dollars.</p>
              </div>
              <div className="mk mk-list">
                <div className="mk-li"><span className="rk">1</span>Pause negative-ROI channels<span className="ef">+$70K/mo</span></div>
                <div className="mk-li"><span className="rk">2</span>Scale Google IAA 5×<span className="ef">+$32K/mo</span></div>
                <div className="mk-li"><span className="rk">3</span>Fix churn 29% → 8%<span className="ef">LTV ×6.2</span></div>
                <div className="mk-li"><span className="rk">4</span>Rebuild the trial onboarding<span className="ef">+$24K/mo</span></div>
                <div className="mk-li"><span className="rk">5</span>Reprice the annual plan<span className="ef">+$18K/mo</span></div>
                <div className="mk-li"><span className="rk">6</span>Win back lapsed subscribers<span className="ef">+$11K/mo</span></div>
                <div className="mk-li"><span className="rk">7</span>Consolidate the analytics stack<span className="ef">−$4K/mo</span></div>
                <div className="mk-li"><span className="rk">8</span>Shift budget to Google IAA geos<span className="ef">+$9K/mo</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* REAL CASE */}
      <section className="block wrap reveal" id="case" style={{ paddingTop: 0 }}>
        <div className="case-grid">
          <div className="case-photo">
            <img src={casePhoto} alt="Founder reviewing analytics" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            <span className="badge"><span className="d"></span>Gemli App — audited</span>
          </div>
          <div className="case-card">
            <div className="case-eyebrow">Real example · Gemli App</div>
            <blockquote className="case-quote">“We were burning <span className="hl">$70K</span> a month on channels that weren't working — and had no idea.”</blockquote>
            <div className="case-stats">
              <div className="case-stat"><CountUp className="cs-n" value={70} prefix="$" suffix="K" /><span className="cs-d">Burned every month on TikTok, AppLovin &amp; Snapchat at a CPA of $427 to $3,183.</span></div>
              <div className="case-stat"><CountUp className="cs-n" value={29} prefix="$" /><span className="cs-d">CPA on Google In-App Actions — a channel that was underfunded by 10×.</span></div>
              <div className="case-stat"><CountUp className="cs-n" value={29} suffix="%" /><span className="cs-d">Monthly churn instead of a 5–8% norm — the main driver behind falling MRR.</span></div>
            </div>
            <div className="case-foot">From a real audit — every metric pulled from the client's own data.</div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="block wrap" id="how">
        <div className="block-head reveal-item">
          <span className="eyebrow">How it works</span>
          <h2 className="sec">Four steps to a finished{' '}report</h2>
        </div>
        <div className="steps">
          <div className="step reveal-item">
            <div className="step-n">01</div>
            <h4>Connect your data</h4>
            <p>RevenueCat, AppsFlyer, Google &amp; Meta Ads via OAuth in 10 minutes. No manual exports.</p>
            <span className="t">~10 min</span>
          </div>
          <div className="step reveal-item">
            <div className="step-n">02</div>
            <h4>Fill the brief</h4>
            <p>8 questions about your model, goals and what you've already tried. Takes 15 minutes.</p>
            <span className="t">~15 min</span>
          </div>
          <div className="step reveal-item">
            <div className="step-n">03</div>
            <h4>We run the analysis</h4>
            <p>AI plus an analyst process the data, build models and benchmark against your industry.</p>
            <span className="t">~1 hour</span>
          </div>
          <div className="step reveal-item">
            <div className="step-n">04</div>
            <h4>Get report + call</h4>
            <p>A PDF report and a 30-minute call with an analyst to walk through the findings.</p>
            <span className="t">PDF + call</span>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="block wrap reveal" id="faq" style={{ maxWidth: 880 }}>
        <span className="eyebrow">Questions</span>
        <h2 className="sec">Frequently asked</h2>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, i) => {
            const open = openFaq === i;
            return (
              <div
                key={item.q}
                className={`faq-item${open ? ' open' : ''}`}
                onClick={() => setOpenFaq(open ? null : i)}
              >
                <div className="faq-q"><span>{item.q}</span><span className="faq-tog"></span></div>
                <div className="faq-a" style={{ maxHeight: open ? 360 : 0 }}>
                  <div className="faq-a-in">{item.a}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FINAL CTA */}
      <LandingFinalCta />

      {/* FOOTER */}
      <LandingFooter />
    </div>
  );
}
